import React from "react";
import { createGlobalStyle, styled } from "styled-components";

/**
 * AB Vault opt-in theme (spec §4.2) — the vault-derived teal `--ab*` palette, applied ONLY to the
 * `/ab-vault/candidate-assignments` route (wrapped there in AppRouter.tsx), preserving that
 * screen's existing visual identity from apps/vault-frontend. Values copied EXACTLY from
 * apps/vault-frontend/src/theme/VaultTheme.tsx's `VaultGlobalStyle` — do not diverge.
 *
 * Deliberately NOT applied globally: `CustomerLayout`'s default theme stays the manager
 * `ColorProvider` dark-navy/blue palette (`CustomerTheme.tsx`) for every other interface. Mounting
 * this provider only around the candidate-assignments route element (not around `CustomerLayout`
 * or the app root) keeps the `--ab*` overrides scoped to that one screen.
 *
 * Styling decision (same as vault's): reuse @cinesys/ui AS-IS — this only defines the
 * SURROUNDING chrome variables the ported screen's own styled-components consume via var(--ab*);
 * it does not override the ColorProvider tokens @cinesys/ui relies on.
 *
 * No `--abBg` here (removed) — the screen's page body is no longer its own dark card; it now
 * shows through to `CustomerLayout`'s ambient `var(--dark060)` background, same as every other
 * customer-stack interface (`AbHeader.tsx`/`CandidateAssignmentsScreen.tsx`). No `--abHeader`
 * either (also removed) — `AbHeader` uses the shared `var(--dark100)` for its own background
 * instead of a one-off hex, same reasoning.
 *
 * Tab identity (browser title) lives in `AbHeader.tsx`, not here — same split as
 * `SpotterThemeProvider` (CSS only) / `SpotterHeader` (branding + tab identity). The favicon is not
 * per-interface at all any more; it is the one CineSys mark in `public/index.html`.
 */
export const VaultGlobalStyle = createGlobalStyle`
  :root {
    --abField: #2d323d;
    --abAccent: #00d5b4;
    --abWarn: #ffb11b;
    --abDanger: #f77066;
    --abLabel: #bbbec1;
  }
`;

/**
 * Re-hues the shared blue ramp (default hue 230°, indigo) to the AB Vault wordmark's own cyan-blue
 * for every ab-vault screen. \`--blue300\` (the one token \`AbHeader\`'s \`HeaderTabs\` actually renders
 * — the active-tab indicator) is pinned to hsl(198, 48%, 52%), landed on after a few rounds of
 * live-feedback tuning between the logo's exact (too-dark/too-saturated) hex and a too-pale
 * computed ramp stop. Every other stop holds that SAME hue/saturation, varying only lightness, so
 * the whole ramp reads as one consistent muted teal-blue family rather than --blue300 being an
 * outlier the rest of the ramp doesn't match.
 *
 * A real wrapping element, NOT part of \`VaultGlobalStyle\`'s \`:root\` block — \`ColorProvider\`'s own
 * tokens are ALSO declared at \`:root\` (via its \`RootTokens\`), so two \`:root\` rules setting the
 * same custom properties tie on specificity and the winner depends on style-tag insertion order,
 * which isn't reliable across route-scoped vs. app-root-scoped global styles. Scoping the override
 * to a descendant element sidesteps that entirely — a value set here always wins over one inherited
 * from an ancestor \`:root\`, no tie possible. \`display: contents\` keeps this div out of layout
 * entirely so it doesn't affect \`CandidateAssignmentsScreen\`'s own height/flex assumptions.
 */
const VaultBlueScope = styled.div`
  display: contents;
  --blueBackground: hsl(198, 48%, 61%, 0.3);
  --blueBorderOverlay: hsl(198, 48%, 61%, 0.17);
  --blueBorder: hsl(198, 48%, 61%, 0.5);
  --blueFocusRing: hsl(198, 48%, 52%, 0.18);
  --blue030: hsl(198, 48%, 28%);
  --blue060: hsl(198, 48%, 33%);
  --blue090: hsl(198, 48%, 38%);
  --blue100: hsl(198, 48%, 42%);
  --blue200: hsl(198, 48%, 46%);
  /* The confirmed anchor — AbHeader's HeaderTabs active-tab indicator. */
  --blue300: hsl(198, 48%, 52%);
  --blue400: hsl(198, 48%, 60%);
  --blue400a: hsla(198, 48%, 60%, 0.2);
  --blue500: hsl(198, 48%, 68%);
`;

interface Props {
  children?: React.ReactNode;
}

const VaultThemeProvider = ({ children }: Props) => (
  <>
    <VaultGlobalStyle />
    <VaultBlueScope>{children}</VaultBlueScope>
  </>
);

export default VaultThemeProvider;
